package com.tixforgigs.common.core.util

import com.tixforgigs.apax.models.*
import com.tixforgigs.common.core.extensions.*
import com.tixforgigs.common.core.imports.SeatingObject
import com.tixforgigs.common.core.models.ShoppingCartInformation
import com.tixforgigs.common.core.util.LocalizationUtil.currentPaymentLocale
import com.tixforgigs.common.core.util.SeatsIoUtil.mHoldToken
import com.tixforgigs.common.core.util.SeatsIoUtil.objectTypeEnum
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mCurrentOrderState
import com.tixforgigs.common.core.util.StorageUtil.LocalStorageState.mShoppingCartState
import com.tixforgigs.common.core.util.StorageUtil.getLocalStorageItem
import com.tixforgigs.common.core.util.StorageUtil.setLocalStorageItem
import com.tixforgigs.common.http.ApiPostPath
import com.tixforgigs.common.http.HttpClient.apiV4Post
import com.tixforgigs.common.react.model.seatsio.SeatObjectType
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.*
import kotlinx.datetime.LocalTime
import kotlin.time.Clock
import kotlin.time.Duration.Companion.seconds
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

object CartUtil {

  val OrderResponse?.isLocked: Boolean
    get() = isNotNull

  val mAvailableShippingMethods = MutableStateFlow(
    StorageUtil.getLocalStorageItem<List<ShippingMethod>?>(
      StorageUtil.StorageKey.AvailableShippingMethods
    )
  )

  val mSelectedShippingMethod = MutableStateFlow(
    StorageUtil.getLocalStorageItem<ShippingMethod?>(
      StorageUtil.StorageKey.SelectedShippingMethod
    )
  )

  val mActiveCartItems
    get() = mShoppingCartState.map { it?.shoppingCart?.items }

  val mCurrentOrderItems
    get() = mCurrentOrderState.map { it?.orderItems }

  val mActiveCartItemsCount = mActiveCartItems.map { item ->
    item?.sumOf { it.quantity }.orElse { 0 }
  }.stateIn(
    scope = CoroutineScope(Dispatchers.Main),
    started = SharingStarted.WhileSubscribed(),
    initialValue = mShoppingCartState.value?.shoppingCart?.items.orEmpty().sumOf { it.quantity }
  )

  val mCurrentOrderItemsCount = mCurrentOrderItems.map { item -> item?.sumOf { it.quantity }.orElse { 0 } }.stateIn(
    scope = CoroutineScope(Dispatchers.Main),
    started = SharingStarted.WhileSubscribed(),
    initialValue = mCurrentOrderState.value?.orderItems.orEmpty().sumOf { it.quantity }
  )

  suspend fun Product.addToCart(
    itemType: ItemType,
    eventId: Int,
    quantity: Int,
    isBestAvailable: Boolean = false,
  ) {
    checkShoppingCart()
    mShoppingCartState.value?.let { cartResponse ->
      ShoppingCartInformation(
        eventId = eventId,
        itemType = itemType,
        product = this,
        quantity = quantity,
        shoppingCart = cartResponse.shoppingCart
      ).let { shoppingCartInformation ->
        if (isBestAvailable) {
          shoppingCartInformation.toBestAvailableRequest(mHoldToken, itemType)
            .let { bestAvailableRequest ->
              apiV4Post<CartResponse>(ApiPostPath.CartFloorplanBestAvailable(bestAvailableRequest))
                ?.let { cart ->
                  setLocalStorageItem<CartResponse>(
                    StorageUtil.StorageKey.ShoppingCart,
                    cart,
                    CartResponse.serializer()
                  )
                }
            }
        } else {
          shoppingCartInformation.toItemAddRequest().let { request ->
            apiV4Post<CartResponse>(ApiPostPath.CartAdd(request))
              ?.let { cart ->
                setLocalStorageItem<CartResponse>(
                  StorageUtil.StorageKey.ShoppingCart,
                  cart,
                  CartResponse.serializer()
                )
              }
          }
        }
      }
    }
  }

  suspend fun addToCart(
    itemType: ItemType,
    productId: Int,
    eventId: Int,
    quantity: Int,
    resaleId: Int? = null,
    resaleToken: String? = null,
  ) {
    checkShoppingCart()
    mShoppingCartState.value?.let { cart ->
      apiV4Post<CartResponse>(
        ApiPostPath.CartAdd(
          ItemAddRequest(
            itemTypeId = itemType.itemTypeId,
            quantity = quantity,
            shoppingCart = cart.shoppingCart,
            eventId = eventId,
            productId = productId,
            resaleId = resaleId,
            resaleToken = resaleToken
          )
        )
      )?.let { cart ->
        setLocalStorageItem<CartResponse>(
          StorageUtil.StorageKey.ShoppingCart,
          cart,
          CartResponse.serializer()
        )
      }
    }
  }

  suspend fun removeFromCart(
    productId: Int,
    itemType: ItemType,
    quantity: Int? = null,
    floorPlanData: FloorPlanData? = null,
  ) {
    mShoppingCartState.value?.let { cart ->
      cart.shoppingCart.items?.firstOrNull { it.productId == productId && it.itemType == itemType }?.cartItemId?.let { cartItemId ->
        ItemRemoveRequest(
          cartItemId = cartItemId,
          shoppingCart = cart.shoppingCart,
          floorPlanData = floorPlanData,
          quantity = quantity
        ).let { removeRequest ->
          apiV4Post<CartResponse>(ApiPostPath.CartRemove(removeRequest))?.let { cart ->
            setLocalStorageItem<CartResponse>(
              StorageUtil.StorageKey.ShoppingCart,
              cart,
              CartResponse.serializer()
            )
          }
        }
      }
    }
  }

  suspend fun CartResponse.refreshShippingMethods() {
    apiV4Post<CartOptionsResponse?>(
      type = ApiPostPath.CartOptions(
        body = CartOptionsRequest(
          countryCode = currentPaymentLocale.countryCode,
          shoppingCart = shoppingCart
        )
      )
    )?.let { cartOptionsResponse ->
      val selectedShippingMethod =
        mAvailableShippingMethods.updateAndGet { cartOptionsResponse.availableShippingMethods }
          ?.let { availableShippingMethods ->
            mSelectedShippingMethod.updateAndGet {
              if (availableShippingMethods.contains(it)) it else availableShippingMethods.firstOrNull()
            }
          }

      apiV4Post<CartResponse>(
        type = ApiPostPath.CartRefresh(
          body = CartRefreshRequest(
            countryCode = currentPaymentLocale.countryCode,
            shoppingCart = shoppingCart,
            shippingMethodId = selectedShippingMethod?.shippingMethodId
          )
        )
      )?.let { cartResponse ->
        setLocalStorageItem<CartResponse>(
          StorageUtil.StorageKey.ShoppingCart,
          cartResponse,
          CartResponse.serializer()
        )
      }
    }
  }

  suspend fun CartResponse.refresh() {
    apiV4Post<CartResponse>(
      type = ApiPostPath.CartRefresh(
        body = CartRefreshRequest(
          countryCode = currentPaymentLocale.countryCode,
          shoppingCart = shoppingCart,
          shippingMethodId = mSelectedShippingMethod.value?.shippingMethodId
        )
      )
    )?.let { cartResponse ->
      setLocalStorageItem<CartResponse>(
        StorageUtil.StorageKey.ShoppingCart,
        cartResponse,
        CartResponse.serializer()
      )
    }
  }

  suspend fun SeatingObject.addToCart(
    eventId: Int,
    itemType: ItemTypeId,
  ) {
    checkShoppingCart()
    mShoppingCartState.value?.let { cartResponse ->
      if (objectAlreadyInCart(cartResponse).not() || objectTypeEnum == SeatObjectType.GA) {
        apiV4Post<CartResponse>(
          ApiPostPath.CartAdd(
            ItemAddRequest(
              eventId = eventId,
              itemTypeId = itemType,
              productId = selectedTicketType.toInt(),
              quantity = 1,
              shoppingCart = cartResponse.shoppingCart,
              floorPlanData = FloorPlanData(
                categoryKey = category.key.toString(),
                objectLabel = label,
                objectType = objectType,
                entry = entrance,
                seat = labels.own,
                row = if (objectTypeEnum == SeatObjectType.SEAT) labels.parent else null,
                section = labels.section
              ),
              holdToken = mHoldToken
            )
          )
        )?.let { cart ->
          setLocalStorageItem<CartResponse>(
            StorageUtil.StorageKey.ShoppingCart,
            cart,
            CartResponse.serializer()
          )
        }
      }
    }
  }

  suspend fun SeatingObject.removeFromCart() {
    mShoppingCartState.value?.let { cart ->
      val cartItem = cart.shoppingCart.items?.firstNotNullOfOrNull { item ->
        item.floorPlanData?.firstOrNull {
          it.objectLabel == label
              && it.objectType.equals(other = objectType, ignoreCase = true)
              && it.categoryKey == category.key.toString()
        }
      }

      cartItem?.cartItemId?.let { cartItemId ->
        apiV4Post<CartResponse>(
          ApiPostPath.CartRemove(
            ItemRemoveRequest(
              cartItemId = cartItemId,
              shoppingCart = cart.shoppingCart,
              floorPlanData = FloorPlanData(
                categoryKey = category.key.toString(), objectLabel = label, objectType = objectType, seat = labels.own
              ),
              quantity = 1
            )
          )
        )?.let { cart ->
          setLocalStorageItem<CartResponse>(
            StorageUtil.StorageKey.ShoppingCart,
            cart,
            CartResponse.serializer()
          )
        }
      }
    }
  }

  @OptIn(ExperimentalTime::class)
  suspend fun shoppingCartExpiredChecker() {
    while (getLocalStorageItem<CartResponse>(StorageUtil.StorageKey.ShoppingCart).isNotNull) {
      val cartResponse = getLocalStorageItem<CartResponse>(StorageUtil.StorageKey.ShoppingCart)
      val remainingSec = cartResponse?.shoppingCart?.reservationExpiresUTC
        .orElse { Clock.System.now() }
        .minus(Clock.System.now())
        .inWholeSeconds
      if (remainingSec <= 0L) {
        StorageUtil.deleteLocalStorageItem(StorageUtil.StorageKey.ShoppingCart)
      }
      delay(1.seconds)
    }
  }

  fun CartResponse.toCartItemOrNull(
    productId: Int,
    resaleId: Int? = null,
  ): ShoppingCartItem? = shoppingCart.items?.find { item ->
    item.productId == productId && when (resaleId) {
      null -> item.resaleId == null || item.resaleId == -1
      else -> item.resaleId == resaleId
    }
  }

  fun CartResponse.toCartItemCount(
    productId: Int,
    resaleId: Int? = null,
  ): Int = toCartItemOrNull(productId, resaleId)?.quantity.orElse { 0 }

  fun CartResponse.toContingentSelectedItemType(
    productId: Int,
    resaleId: Int? = null,
  ): ItemType? = toCartItemOrNull(productId, resaleId)?.itemType

  @OptIn(ExperimentalTime::class, ExperimentalCoroutinesApi::class)
  fun shoppingCartInvalidationFlow(
    onInvalidate: suspend () -> Unit
  ): Flow<LocalTime?> = mShoppingCartState
    .onStart { emit(mShoppingCartState.value) }
    .distinctUntilChanged()
    .flatMapLatest { cart ->
      if (cart == null || cart.hasItems().not()) {
        flowOf(null)
      } else {
        countdownLocalTimeFlow(cart.expiresAtOrNow())
          .onCompletion { error ->
            val cart = mShoppingCartState.value
            if (cart.expired() && cart.hasItems()) {
              onInvalidate()
            }
          }
      }
    }
    .catch {
      emit(null)
    }

  internal suspend fun checkShoppingCart() {
    if (mShoppingCartState.value.isNull) {
      apiV4Post<CartResponse>(
        ApiPostPath.CartCreate(CartMetaInfo(templateId = IdHelper.templateId)),
      )?.let { cart ->
        StorageUtil.deleteLocalStorageItem(StorageUtil.StorageKey.CurrentOrderMetadata)
        StorageUtil.deleteLocalStorageItem(StorageUtil.StorageKey.CurrentOrder)
        setLocalStorageItem<CartResponse>(
          StorageUtil.StorageKey.ShoppingCart,
          cart,
          CartResponse.serializer()
        )
      }
    }
  }

  internal fun SeatingObject.objectAlreadyInCart(
    cart: CartResponse,
  ): Boolean = cart.shoppingCart.items.orEmpty().any {
    it.floorPlanData.orEmpty().any { floorPlanData ->
      floorPlanData.objectLabel == label && floorPlanData.objectType == objectType && floorPlanData.categoryKey == category.key.toString()
    }
  }

  @OptIn(ExperimentalTime::class)
  private fun countdownLocalTimeFlow(target: Instant): Flow<LocalTime?> = flow {
    while (true) {
      val remainingSec = (target - Clock.System.now()).inWholeSeconds
      if (remainingSec < 0L) {
        emit(null)
        break
      }
      emit(LocalTime.fromSecondOfDay(remainingSec.toInt()))
      delay(1.seconds)
    }
  }

  private fun CartResponse?.hasItems(): Boolean =
    this?.shoppingCart?.items.orEmpty().isNotEmpty()

  @OptIn(ExperimentalTime::class)
  private fun CartResponse?.expired(): Boolean =
    this?.shoppingCart?.reservationExpiresUTC?.let { it <= Clock.System.now() } == true

  @OptIn(ExperimentalTime::class)
  fun CartResponse?.expiresAtOrNow(): Instant =
    this?.shoppingCart?.reservationExpiresUTC.orElse { Clock.System.now() }

  @OptIn(ExperimentalTime::class)
  fun CartResponse?.expiresDateToLocalTimeOrNull(): LocalTime? = this?.let { cart ->
    cart.expiresAtOrNow().minus(Clock.System.now()).inWholeSeconds.let { diffSeconds ->
      if (diffSeconds > 0) LocalTime.fromSecondOfDay(diffSeconds.toInt()) else null
    }
  }
}